import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ArcadeLink, ArcadeLinkService } from '../../services/arcade-link/arcade-link.service';
import { LocalStorageService } from '../../services/local-storage/localstorage.service';
import { UserService } from '../../services/user/user-service';
import { CallerModuleService } from '../../../core/services/caller-module.service';
import { forkJoin, of } from 'rxjs';
import { map, finalize } from 'rxjs/operators';

const USER_TYPE_CACHE_KEY = 'cachedUserType';

@Component({
  selector: 'app-arcade-link-panel',
  templateUrl: './arcade-link-panel.component.html',
  styleUrls: ['./arcade-link-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ArcadeLinkPanelComponent implements OnInit {
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private arcadeLinkService = inject(ArcadeLinkService);
  private localStorageService = inject(LocalStorageService);
  private userService = inject(UserService);
  private callerModuleService = inject(CallerModuleService);

  links: ArcadeLink[] = [];
  searchText = '';
  loading = false;
  selectedCategory = 'All';

  private currentUserType = 1;

  ngOnInit(): void {
    this.loading = true;
    const userPreferences = this.localStorageService.getItem<any>('userPreferences');
    const userId = userPreferences?.userId;
    const cachedUserType = this.localStorageService.getItem<number>(USER_TYPE_CACHE_KEY);

    const userType$ = cachedUserType != null
      ? of(cachedUserType)
      : (() => {
          this.callerModuleService.setCaller('interactive-guide');
          return this.userService.getUser(userId).pipe(
            map((user: any) => {
              const userType = user?.userType ?? 1;
              this.localStorageService.setItem(USER_TYPE_CACHE_KEY, userType);
              return userType;
            }),
            finalize(() => this.callerModuleService.clearCaller()),
          );
        })();

    forkJoin({
      links: this.arcadeLinkService.getAllArcadeLinks(),
      userType: userType$,
    }).subscribe({
      next: ({ links, userType }) => {
        this.currentUserType = userType;
        this.links = links;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  get categories(): string[] {
    const cats = this.links
      .map(l => l.category)
      .filter((c): c is string => !!c);
    return ['All', ...new Set(cats)];
  }

  filteredLinks(): ArcadeLink[] {
    return this.links.filter(link => {
      const matchesVisibility = link.visibleto === 3 || link.visibleto === this.currentUserType;
      const matchesSearch = !this.searchText ||
        link.name.toLowerCase().includes(this.searchText.toLowerCase()) ||
        link.title.toLowerCase().includes(this.searchText.toLowerCase()) ||
        (link.description ?? '').toLowerCase().includes(this.searchText.toLowerCase());
      const matchesCategory = this.selectedCategory === 'All' || link.category === this.selectedCategory;
      return matchesVisibility && matchesSearch && matchesCategory;
    });
  }

  openGuide(link: ArcadeLink) {
    this.router.navigate(['/interactive-guide/view'], {
      queryParams: { url: link.link, name: link.name }
    });
  }

}
